// oxlint-disable react/only-export-components
import { faSpinner } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"
import type { ComponentProps } from "react"
import { cn } from "cn"

const SECONDARY_VARIANTS = [
  "secondary-blue",
  "secondary-blue-on-grey",
  "secondary-red",
  "secondary-red-on-grey",
] as const

const buttonVariants = cva(
  "group/button relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-1 border border-transparent bg-clip-padding font-semibold whitespace-nowrap transition-colors outline-none select-none focus-visible:ring-2 focus-visible:ring-neutrals-v2-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        "primary-pill":
          "rounded-full bg-brand-red-200 text-neutrals-v2-100 hover:bg-brand-red-300 disabled:bg-neutrals-v2-300 disabled:text-neutrals-v2-600 disabled:hover:bg-neutrals-v2-300",
        "primary-rounded":
          "rounded-lg bg-brand-red-200 text-neutrals-v2-100 hover:bg-brand-red-300 disabled:bg-neutrals-v2-300 disabled:text-neutrals-v2-600 disabled:hover:bg-neutrals-v2-300",
        "secondary-blue":
          "rounded-full bg-neutrals-v2-200 text-labels-primary hover:bg-neutrals-v2-300 disabled:bg-neutrals-v2-200 disabled:text-neutrals-v2-600 disabled:hover:bg-neutrals-v2-200",
        "secondary-blue-on-grey":
          "rounded-full bg-neutrals-v2-300 text-labels-primary hover:bg-neutrals-v2-400 disabled:bg-neutrals-v2-300 disabled:text-neutrals-v2-600 disabled:hover:bg-neutrals-v2-300",
        "secondary-red":
          "rounded-full bg-neutrals-v2-200 text-brand-red-200 hover:bg-neutrals-v2-300 disabled:bg-neutrals-v2-200 disabled:text-neutrals-v2-600 disabled:hover:bg-neutrals-v2-200",
        "secondary-red-on-grey":
          "rounded-full bg-neutrals-v2-300 text-brand-red-200 hover:bg-neutrals-v2-400 disabled:bg-neutrals-v2-300 disabled:text-neutrals-v2-600 disabled:hover:bg-neutrals-v2-300",
        outline:
          "rounded-full border-neutrals-v2-300 text-labels-primary hover:bg-neutrals-v2-200 disabled:bg-transparent disabled:text-neutrals-v2-600 disabled:hover:bg-transparent",
        "outline-on-grey":
          "rounded-full border-neutrals-v2-400 text-labels-primary hover:bg-neutrals-v2-300 disabled:bg-transparent disabled:text-neutrals-v2-600 disabled:hover:bg-transparent",
        ghost:
          "rounded-full text-labels-primary hover:bg-neutrals-v2-200 disabled:bg-transparent disabled:text-neutrals-v2-600 disabled:hover:bg-transparent",
        "ghost-on-grey":
          "rounded-full text-labels-primary hover:bg-neutrals-v2-300 disabled:bg-transparent disabled:text-neutrals-v2-600 disabled:hover:bg-transparent",
        link: "rounded-md text-brand-red-200 hover:text-brand-red-300 disabled:text-neutrals-v2-600",
      },
      size: {
        medium: "text-sm leading-5 [&_svg:not([class*='size-'])]:size-[22px]",
        small: "text-xs leading-4 [&_svg:not([class*='size-'])]:size-[18px]",
      },
      iconOnly: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      { size: "medium", iconOnly: false, class: "h-9 px-4" },
      { size: "medium", iconOnly: true, class: "size-9 p-2" },
      { size: "small", iconOnly: false, class: "h-8 px-3" },
      { size: "small", iconOnly: true, class: "size-8 p-2" },
      // Icon-only Sekundärvarianten sind im Design abgerundete Quadrate statt Pills
      { variant: [...SECONDARY_VARIANTS], iconOnly: true, class: "rounded-lg" },
      {
        size: "medium",
        variant: [...SECONDARY_VARIANTS, "outline", "outline-on-grey"],
        class: "[&_svg:not([class*='size-'])]:size-5",
      },
      {
        variant: "link",
        class:
          "h-auto border-0 px-0 py-2 disabled:hover:text-neutrals-v2-600 [&_svg:not([class*='size-'])]:size-[18px]",
      },
    ],
    defaultVariants: {
      variant: "primary-pill",
      size: "medium",
      iconOnly: false,
    },
  }
)

export type ButtonVariant = NonNullable<
  VariantProps<typeof buttonVariants>["variant"]
>

export type ButtonSize = NonNullable<
  VariantProps<typeof buttonVariants>["size"]
>

export type ButtonProps = ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    /** Blendet den Inhalt aus, zeigt einen Spinner und deaktiviert den Button. */
    loading?: boolean
  }

function Button({
  className,
  variant = "primary-pill",
  size = "medium",
  iconOnly = false,
  loading = false,
  asChild = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button"
  const isDisabled = disabled || loading

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-icon-only={iconOnly || undefined}
      data-loading={loading || undefined}
      aria-busy={loading || undefined}
      aria-disabled={isDisabled || undefined}
      disabled={isDisabled}
      className={cn(
        buttonVariants({ variant, size, iconOnly, className }),
        // Inhalt bleibt im Layout, damit die Breite beim Laden nicht springt.
        // `!` ist nötig, da `disabled:text-*` sonst die Textfarbe zurückholt.
        loading &&
          "text-transparent! [&>*:not([data-slot=button-spinner])]:invisible",
        loading &&
          !iconOnly &&
          variant !== "link" &&
          (size === "medium" ? "min-w-[76px]" : "min-w-[68px]")
      )}
      {...props}
    >
      <Slot.Slottable>{children}</Slot.Slottable>
      {loading && (
        <FontAwesomeIcon
          data-slot="button-spinner"
          icon={faSpinner}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-spin text-neutrals-v2-500"
        />
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
